import React from 'react';
import FetchRuntimeTileData from '@components/runtime-components/fetch-runtime-tile-data';
import Tile from '@components/tile/tile';
import Translate from '@utils/translation-functions';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { generateKey } from '@utils/utils';
import { TileBlockProps } from './tile-block.types';
import { Carousel } from '@components/carousel/carousel';
import {
  GridStructure,
  TileBlockContainer,
  TileBlockCTA,
  TileBlockDescription,
  TileBlockTitle,
  TilesGrid,
  TitleDescriptionWrapper,
} from './tile-block.styles';
import { TileProps } from '@components/tile/tile.types';
import useWindowSize from '@hooks/use-window-size';

interface TileHandlerProps {
  tile: TileProps;
  // tiles are 'compact' if device is mobile and not rendered in carousel
  isCompact?: boolean;
  hasBlockTitle?: boolean;
}

const TileHandler = ({ tile, isCompact, hasBlockTitle }: TileHandlerProps) => {
  if (tile.renderType === 'standard') {
    return (
      <Tile {...tile} isCompact={isCompact} hasBlockTitle={hasBlockTitle} />
    );
  } else if (tile.renderType === 'runtime') {
    return (
      <FetchRuntimeTileData
        {...tile}
        isCompact={isCompact}
        hasBlockTitle={hasBlockTitle}
      />
    );
  }

  console.warn(`tileBlock -> tile.renderType not matched: `, tile.renderType);
  return null;
};

const TileBlock: React.FC<TileBlockProps> = ({
  visibleTilesCount = 5,
  title = '',
  anchorId,
  description = '',
  tiles,
  cta,
  className,
  isCarousel,
  translate = true,
}) => {
  const titleTranslated = Translate(title, translate);
  const { width } = useWindowSize();
  const isMobile = !!width && width < 768;
  if (!tiles || tiles.length < 1) return null;
  const isCompact = isMobile && !isCarousel && visibleTilesCount > 3;

  return (
    <TileBlockContainer
      id={anchorId}
      data-testid="tile-block"
      className={className}
    >
      <GridStructure>
        <TitleDescriptionWrapper>
          {titleTranslated && (
            <TileBlockTitle>{titleTranslated}</TileBlockTitle>
          )}
          {description && (
            <TileBlockDescription>{description}</TileBlockDescription>
          )}
        </TitleDescriptionWrapper>
        {cta && cta.text && cta.url && (
          <TileBlockCTA
            dataTestId="tile-block-cta"
            gtmId={GTM_ID.TILE_BLOCK_CTA}
            url={cta.url}
          >
            {cta.text}
          </TileBlockCTA>
        )}
      </GridStructure>
      {isCarousel ? (
        <Carousel
          visibleTilesCount={visibleTilesCount}
          items={tiles.map((tile, index) => (
            <TileHandler
              tile={tile}
              key={generateKey(tile.title, index)}
              hasBlockTitle={!!titleTranslated}
            />
          ))}
        />
      ) : (
        <TilesGrid tilesCount={visibleTilesCount}>
          {tiles.map((tile, index) => (
            <TileHandler
              tile={tile}
              key={generateKey(tile.title, index)}
              isCompact={isCompact}
              hasBlockTitle={!!titleTranslated}
            />
          ))}
        </TilesGrid>
      )}
    </TileBlockContainer>
  );
};

export default TileBlock;
